import { gqlClient } from 'services/api/gql/gqlClient';
import { AddToPromotion } from 'constants/gql/mutations/advocacy/addToPromotion.gql';
import { ERROR } from 'components/Campaigns/Referrer/constants';

/**
 * Enrolls the signed-in referee into the BAF/FnF campaign via the AddToPromotion GraphQL mutation.
 * This is the RMS-side write that allocates a per-user coupon from PVS — burning the referee's
 * one-shot redemption for this share link. The `enrollmentToken` (from a prior
 * validateCampaignReferral call) is required for anti-abuse validation. Response shape is
 * normalized to the REST envelope the ctrlr expects (`responseStatus: 200`), with date fields
 * aliased for consumer symmetry (`clientPromoStartDate` → `startDate`, `clientExpiryDate` →
 * `expirationDate`). Null result payloads are converted to `CUSTOM_ADVOCACY_DOWN` to avoid
 * stranding the user on a blank loading screen.
 *
 * @param {Object} payload - Enrollment payload assembled by the ctrlr from validate + user data.
 * @param {string} payload.referrerCode - Rouge member's referrer code from the share link.
 * @param {string} payload.campaignCode - Campaign identifier (e.g. `hsebaf2025`).
 * @param {string} payload.refereeUsaId - Signed-in referee's BI account ID.
 * @param {string} payload.checksum - Signed URL checksum from the share link.
 * @param {string} payload.refereeEmail - Referee's profile email.
 * @param {string} payload.refereeFirstName - Referee's profile first name.
 * @param {string} payload.refereeLastName - Referee's profile last name.
 * @param {string} payload.registrationDate - Referee's BI registration date (ISO string).
 * @param {string|null} referralToken - Enrollment token issued by validateCampaignReferral.
 * @returns {Promise<Object>} `{ responseStatus: 200, enrolled, couponCode, startDate, expirationDate, ... }`.
 * @throws {{ errors: [{ errorCode: string }] }} `CUSTOM_ADVOCACY_DOWN` on GraphQL error or empty payload.
 */
async function addToPromotion(payload, referralToken) {
    const { country, language } = Sephora.renderQueryParams;
    const {
        referrerCode, campaignCode, refereeUsaId, checksum, refereeEmail, refereeFirstName, refereeLastName, registrationDate
    } = payload;
    const requestedTimestamp = new Date().toISOString();

    try {
        const data = await gqlClient.query({
            headers: { 'x-source': 'ufe' },
            operationName: 'AddToPromotion',
            query: AddToPromotion,
            // Tolerate campaignType enum-serialization errors (backend bug); ctrlr reads campaignType from validate, not here.
            allowPartialResponse: true,
            variables: {
                input: {
                    referrerCode,
                    campaignCode,
                    country,
                    channel: 'DIGITAL',
                    device: 'WEB',
                    refereeUsaId,
                    checksum,
                    requestedTimestamp,
                    refereeEmail,
                    refereeFirstName,
                    refereeLastName,
                    language: language?.toUpperCase(),
                    registrationDate
                },
                enrollmentToken: referralToken || null
            }
        });

        const result = data?.addToPromotion;

        // Empty payload → stranded loading screen; coerce to CUSTOM_ADVOCACY_DOWN.
        if (!result) {
            throw { errors: [{ errorCode: ERROR.CUSTOM_ADVOCACY_DOWN }] };
        }

        // Synthesize the REST envelope shape the ctrlr expects (responseStatus:200 + aliased dates).
        return {
            responseStatus: 200,
            ...result,
            startDate: result.clientPromoStartDate || null,
            expirationDate: result.clientExpiryDate || null
        };
    } catch (err) {
        // Log only err.message; the full error object may carry PII (refereeEmail, refereeFirstName, refereeLastName).
        Sephora.logger.warn('[advocacy/addToPromotion] GraphQL error, degrading to CUSTOM_ADVOCACY_DOWN:', err?.message);
        throw { errors: [{ errorCode: ERROR.CUSTOM_ADVOCACY_DOWN }] };
    }
}

export default addToPromotion;
